/**
 * Manual check-in item catalog + entry mapping — the 15 things a person (or a
 * visiting caregiver) can record from the wellness check-in sheet, and the
 * translation from sheet values to the server's /wellness/manual-entry
 * contract (server type names, units, transforms like sleep hours → minutes).
 *
 * Plain module, no React: the sheet renders FROM this catalog, jest covers
 * the mapping, and the server re-validates everything against its own sanity
 * ranges (client ranges here exist for inline feedback, not enforcement).
 *
 * The fifteenth item — medications — deliberately is NOT a row here: med
 * marks belong to the adherence system (dose_self_marked / caregiver flows),
 * which the sheet links out to rather than duplicating a second write path.
 */

import { formatSignalValue, measuredAt } from './wellnessBrainFormat';

export type CheckInKind = 'measure' | 'pair' | 'rating' | 'yesno' | 'count';

export type CheckInItem = {
  key: string;
  label: string;
  icon: [string, string];
  kind: CheckInKind;
  /** Display unit ("mmHg", "°F", "%"…), where one exists. */
  unit?: string;
  /** Inline range feedback (server re-validates with its own bounds). */
  min: number;
  max: number;
  /** pair: second field's range (diastolic). */
  minB?: number;
  maxB?: number;
  /** rating: lowest selectable (pain starts at 0, mood at 1). */
  ratingMin?: number;
  placeholder?: string;
  placeholderB?: string;
  /** Server type key(s). pair items map [primary, secondary]. */
  serverType?: string;
  serverTypes?: [string, string];
  /** Unit string sent to the server when it differs from the display unit. */
  serverUnit?: string;
  /** Value transform before sending (e.g. sleep hours → minutes). */
  toServer?: (v: number) => number;
  /** Decimal input allowed (temperature 98.6); integers otherwise. */
  decimal?: boolean;
};

export type CheckInSection = { title: string; items: CheckInItem[] };

export const CHECK_IN_SECTIONS: CheckInSection[] = [
  {
    title: 'Vitals',
    items: [
      {
        key: 'blood_pressure', label: 'Blood pressure', icon: ['fad', 'heart-pulse'], kind: 'pair',
        unit: 'mmHg', min: 60, max: 260, minB: 30, maxB: 160,
        placeholder: '120', placeholderB: '80',
        serverTypes: ['BloodPressureSystolic', 'BloodPressureDiastolic'], serverUnit: 'mmHg',
      },
      {
        key: 'heart_rate', label: 'Heart rate', icon: ['fad', 'heart'], kind: 'measure',
        unit: 'bpm', min: 25, max: 220, placeholder: '68', serverType: 'HeartRate', serverUnit: 'bpm',
      },
      {
        key: 'temperature', label: 'Temperature', icon: ['fad', 'temperature-half'], kind: 'measure',
        unit: '°F', min: 90, max: 108, placeholder: '98.6', serverType: 'BodyTemperature', serverUnit: 'degF', decimal: true,
      },
      {
        key: 'spo2', label: 'Blood oxygen', icon: ['fad', 'lungs'], kind: 'measure',
        unit: '%', min: 60, max: 100, placeholder: '97', serverType: 'OxygenSaturation', serverUnit: '%',
      },
      {
        key: 'glucose', label: 'Blood glucose', icon: ['fad', 'droplet'], kind: 'measure',
        unit: 'mg/dL', min: 20, max: 600, placeholder: '105', serverType: 'BloodGlucose', serverUnit: 'mg/dL',
      },
      {
        key: 'weight', label: 'Weight', icon: ['fad', 'weight-scale'], kind: 'measure',
        unit: 'lb', min: 50, max: 700, placeholder: '150', serverType: 'BodyMass', serverUnit: 'lb', decimal: true,
      },
    ],
  },
  {
    title: 'How the day feels',
    items: [
      {
        key: 'sleep', label: 'Sleep last night', icon: ['fad', 'bed'], kind: 'measure',
        unit: 'hours', min: 0, max: 18, placeholder: '7.5', serverType: 'SleepAnalysis', serverUnit: 'min',
        toServer: (hours: number) => Math.round(hours * 60), decimal: true,
      },
      { key: 'pain', label: 'Pain', icon: ['fad', 'wave-pulse'], kind: 'rating', min: 0, max: 10, ratingMin: 0, serverType: 'PainLevel', serverUnit: 'score' },
      { key: 'mood', label: 'Mood', icon: ['fad', 'face-smile'], kind: 'rating', min: 1, max: 5, ratingMin: 1, serverType: 'MoodRating', serverUnit: 'score' },
      { key: 'energy', label: 'Energy', icon: ['fad', 'bolt'], kind: 'rating', min: 1, max: 5, ratingMin: 1, serverType: 'EnergyLevel', serverUnit: 'score' },
      { key: 'appetite', label: 'Appetite', icon: ['fad', 'utensils'], kind: 'rating', min: 1, max: 5, ratingMin: 1, serverType: 'AppetiteRating', serverUnit: 'score' },
      { key: 'dizziness', label: 'Dizzy today', icon: ['fad', 'face-pensive'], kind: 'yesno', min: 0, max: 1, serverType: 'DizzinessEpisode', serverUnit: 'score' },
    ],
  },
  {
    title: 'Day counts',
    items: [
      { key: 'water', label: 'Water', icon: ['fad', 'glass-water'], kind: 'count', unit: 'glasses', min: 0, max: 30, serverType: 'HydrationGlasses', serverUnit: 'glasses' },
      { key: 'falls', label: 'Falls today', icon: ['fad', 'person-falling'], kind: 'count', min: 0, max: 20, serverType: 'NumberOfTimesFallen', serverUnit: 'count' },
    ],
  },
];

export const ALL_CHECK_IN_ITEMS: CheckInItem[] = CHECK_IN_SECTIONS.flatMap((s) => s.items);

/**
 * One catalog item, projected for the watch.
 *
 * The watch does not carry its own copy of this table. Fourteen items with
 * units, sanity ranges and exact server type names ("BloodPressureSystolic",
 * "HydrationGlasses") hand-transcribed into Swift would drift the first time
 * anyone edits one side, and drift here is not cosmetic: a wrong unit string
 * is a rejected entry, and a wrong range is a reading the watch accepts and
 * the server refuses. Same reasoning the wellness rows already follow, where
 * the phone sends a pre-formatted value and a colour rather than a second
 * formatting table on the watch.
 */
export type WatchCheckInItem = {
  key: string;
  label: string;
  /** SF Symbol. Chosen against watchOS 9, this app's minimum. */
  symbol: string;
  kind: CheckInKind;
  /** Display unit, for the entry screen. */
  unit?: string;
  /** Unit string the server validates against — often differs from display. */
  serverUnit?: string;
  /** Server type key, or the pair [systolic, diastolic]. */
  serverType?: string;
  serverTypes?: [string, string];
  min: number;
  max: number;
  minB?: number;
  maxB?: number;
  ratingMin?: number;
  /** Digital Crown increment: whole numbers, or tenths where decimals apply. */
  step: number;
  /**
   * Where the crown STARTS once the user engages it — deliberately not a
   * default value. The entry screen shows "—" and keeps Save disabled until
   * the crown moves or a control is tapped, so nothing can be recorded that
   * the user did not choose. That property is the whole reason the phone sheet
   * was rebuilt; a watch that submits 120/80 because that is where the dial
   * happened to rest would reintroduce it in a worse place.
   */
  crownStart: number;
  crownStartB?: number;
  /** Hours → minutes and the like, applied on the phone after the value lands. */
  scaleToServer?: number;
};

/** SF Symbols for the watch, against watchOS 9. Kept beside the catalog so a
 *  new item declares its symbol in one place with everything else about it. */
const WATCH_SYMBOLS: Record<string, string> = {
  blood_pressure: 'heart.text.square',
  heart_rate: 'heart.fill',
  temperature: 'thermometer',
  spo2: 'lungs.fill',
  glucose: 'drop.fill',
  weight: 'scalemass.fill',
  sleep: 'bed.double.fill',
  pain: 'waveform.path.ecg',
  mood: 'face.smiling',
  energy: 'bolt.fill',
  appetite: 'fork.knife',
  dizziness: 'arrow.clockwise',
  water: 'cup.and.saucer.fill',
  falls: 'figure.fall',
};

/**
 * Wrist-fit order: cheapest interaction first.
 *
 * The phone groups by meaning (Vitals / How the day feels / Day counts), which
 * is right on a screen you can scan. A watch list is read a few rows at a
 * time, so the useful order is by how much work each one costs: a tap, then a
 * couple of taps, then a crown spin, then two crown spins. Pain and mood are
 * daily and cost one tap; blood pressure is occasional and costs the most, so
 * it sits last rather than first purely because it is a vital.
 */
const WATCH_ORDER = [
  'pain', 'mood', 'energy', 'appetite', 'dizziness',
  'water', 'falls',
  'temperature', 'glucose', 'weight', 'sleep', 'heart_rate', 'spo2',
  'blood_pressure',
];

/** Parsed from the phone's placeholder — a plausible resting point for the
 *  crown, never a submitted value. Ratings and counts start at their floor. */
function crownStartFor(item: CheckInItem): number {
  const fromPlaceholder = item.placeholder != null ? Number(item.placeholder) : NaN;
  if (Number.isFinite(fromPlaceholder)) return fromPlaceholder;
  return item.ratingMin ?? item.min;
}

/** The catalog the watch renders from. */
export function buildWatchCheckInCatalog(): WatchCheckInItem[] {
  const byKey = new Map(ALL_CHECK_IN_ITEMS.map((i) => [i.key, i]));
  // Ordered list first, then anything the order forgot — a new catalog row
  // should show up on the watch even if nobody remembered to place it, rather
  // than silently vanishing from one surface.
  const ordered = [
    ...WATCH_ORDER.map((k) => byKey.get(k)).filter(Boolean) as CheckInItem[],
    ...ALL_CHECK_IN_ITEMS.filter((i) => !WATCH_ORDER.includes(i.key)),
  ];

  return ordered.map((item) => ({
    key: item.key,
    label: item.label,
    symbol: WATCH_SYMBOLS[item.key] || 'circle',
    kind: item.kind,
    ...(item.unit ? { unit: item.unit } : {}),
    ...(item.serverUnit ? { serverUnit: item.serverUnit } : {}),
    ...(item.serverType ? { serverType: item.serverType } : {}),
    ...(item.serverTypes ? { serverTypes: item.serverTypes } : {}),
    min: item.min,
    max: item.max,
    ...(item.minB != null ? { minB: item.minB } : {}),
    ...(item.maxB != null ? { maxB: item.maxB } : {}),
    ...(item.ratingMin != null ? { ratingMin: item.ratingMin } : {}),
    step: item.decimal ? 0.1 : 1,
    crownStart: crownStartFor(item),
    ...(item.kind === 'pair'
      ? { crownStartB: item.placeholderB != null ? Number(item.placeholderB) : (item.minB ?? item.min) }
      : {}),
    // Sleep is the only transform in the catalog and the watch must not apply
    // it — the phone does, on the way out, using the same toServer the sheet
    // uses. Sent so the watch can SHOW hours while the server gets minutes.
    ...(item.key === 'sleep' ? { scaleToServer: 60 } : {}),
  }));
}

/**
 * What the list row shows for an item the user has filled in — "120/80 mmHg",
 * "7/10", "Yes", "3 glasses" — or null when they have not touched it.
 *
 * The list exists so that nothing on screen looks like a value unless it IS
 * one. An untouched row says "Not recorded" and shows no input at all; the
 * previous single-sheet layout put a grey "120" and "80" inside blood-pressure
 * boxes, which reads as a reading somebody took rather than a hint about
 * format. That ambiguity is worth avoiding anywhere, and doubly so on a screen
 * whose whole job is recording real measurements.
 *
 * A half-filled pair returns "120/—" rather than null: the row has been
 * touched, saving is blocked until it is finished, and the row has to say why.
 */
export function formatEnteredValue(item: CheckInItem, v: { a?: number | null; b?: number | null } | undefined): string | null {
  const a = v?.a;
  const b = v?.b;
  if (a == null && b == null) return null;

  const num = (n: number) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10));

  switch (item.kind) {
    case 'pair':
      return `${a == null ? '—' : num(a)}/${b == null ? '—' : num(b)}${item.unit ? ` ${item.unit}` : ''}`;
    case 'rating':
      return a == null ? null : `${num(a)}/${item.max}`;
    case 'yesno':
      return a == null ? null : (a >= 1 ? 'Yes' : 'No');
    default:
      return a == null ? null : `${num(a)}${item.unit ? ` ${item.unit}` : ''}`;
  }
}

/** Shape of the wellness brain this module reads — structural, to avoid a
 *  dependency on the component-facing types. */
type BrainLike = {
  body_signals?: Array<any>;
  checkin_signals?: Array<any>;
} | null | undefined;

/**
 * The last reading already on file for an item — "118/76" plus how long ago —
 * or null when there is none.
 *
 * Shown under the input while recording, because the question a person
 * actually has at that moment is "what was it last time?", and because it
 * quietly answers the other one: seeing a previous entry sitting there makes
 * it obvious that this screen appends to a history rather than overwriting a
 * single current value.
 *
 * Formatting is delegated to wellnessBrainFormat so a reading renders
 * identically here and on the Wellness screen — including the cases that are
 * easy to get wrong alone, like sleep being stored in minutes and shown as
 * "7h 20m", or a blood pressure needing its diastolic to mean anything.
 */
export function lastRecorded(item: CheckInItem, brain: BrainLike, nowMs?: number): { value: string; when: string } | null {
  const key = item.serverTypes ? item.serverTypes[0] : item.serverType;
  if (!key) return null;
  const signals = [...(brain?.body_signals || []), ...(brain?.checkin_signals || [])];
  const sig = signals.find((s) => s?.key === key);
  if (!sig?.latest || sig.latest.value == null) return null;

  const value = formatSignalValue(sig);
  if (!value) return null;
  return { value, when: measuredAt(sig.latest.at, nowMs) };
}

/** Sheet value state: `a` for every kind, `b` only for pair (diastolic). */
export type CheckInValues = Record<string, { a?: number | null; b?: number | null }>;

export type CheckInEntry = { type: string; value: number; unit?: string };

export type BuildResult = {
  entries: CheckInEntry[];
  /** Item keys with a value outside inline bounds (or half a pair) — nothing sends while any exist. */
  invalid: string[];
};

const inRange = (v: number, min: number, max: number) => Number.isFinite(v) && v >= min && v <= max;

/**
 * Values → server entries. Untouched items are skipped; a half-filled blood
 * pressure or an out-of-bounds value marks the ITEM invalid without touching
 * its siblings, so the sheet can highlight exactly what needs fixing.
 */
export function buildEntries(values: CheckInValues): BuildResult {
  const entries: CheckInEntry[] = [];
  const invalid: string[] = [];

  for (const item of ALL_CHECK_IN_ITEMS) {
    const v = values[item.key];
    const a = v?.a;
    const b = v?.b;
    const touched = a != null || b != null;
    if (!touched) continue;

    if (item.kind === 'pair') {
      if (a == null || b == null || !inRange(a, item.min, item.max) || !inRange(b, item.minB ?? item.min, item.maxB ?? item.max)) {
        invalid.push(item.key);
        continue;
      }
      const [primary, secondary] = item.serverTypes as [string, string];
      entries.push({ type: primary, value: a, unit: item.serverUnit });
      entries.push({ type: secondary, value: b, unit: item.serverUnit });
      continue;
    }

    if (a == null || !inRange(a, item.min, item.max)) {
      invalid.push(item.key);
      continue;
    }
    const value = item.toServer ? item.toServer(a) : a;
    entries.push({ type: item.serverType as string, value, unit: item.serverUnit });
  }

  return { entries, invalid };
}
